<template>
  <div class="content-filter">
    <form
      v-if="showSearch"
      class="content-filter__search-bar"
      :class="{
        [$class('active')]: isActive,
      }"
    >
      <label
        class="visually-hidden"
        for="content-search"
        v-text="$string('plans.search_placeholder')"
      />

      <input
        id="content-search"
        :value="searchQuery"
        type="search"
        :placeholder="$string('plans.search_placeholder')"
        name="q"
        class="content-filter__search-bar__input text-mobile-paragraph-m-medium text-desktop-paragraph-s-medium"
        @input="$emit('update-search-query', $event.target.value)"
        @focus="isActive = true"
        @blur="isActive = false"
      />
      <div class="content-filter__search-bar__submit" type="submit">
        <span
          class="visually-hidden"
          v-text="$string('general.site_header.search')"
        />

        <icon-search-desktop />
      </div>
      <button
        class="content-filter__search-bar__clear"
        :class="[{ [$class('active')]: searchQuery.length > 0 }]"
        type="button"
        @click="$emit('update-search-query', '')"
      >
        <span
          class="visually-hidden"
          v-text="$string('general.site_header.clear_search')"
        />
        <icon-close />
      </button>
    </form>

    <template v-if="showFilter">
      <div class="content-filter__row">
        <button
          id="content-drawer"
          class="content-filter__button icon-button"
          type="button"
          @click.prevent="openFilterOverlay"
        >
          <icon-filter />

          <p
            class="text-mobile-paragraph-m-bold text-desktop-paragraph-m-bold"
            v-text="$string('plans.filter')"
          />
        </button>

        <btn
          v-if="hasActiveFilters"
          :label="$string('plans.reset_filter')"
          class="content-filter__end text-mobile-paragraph-m-regular-link text-desktop-paragraph-m-regular-link"
          modifiers="text"
          size="medium-medium"
          @click="$emit('reset-filters')"
        />
      </div>

      <div v-if="hasActiveFilters" class="content-filter__row">
        <div v-for="(values, key) of filters" :key="key" class="filter-group">
          <div
            v-for="value of values"
            :key="`${key}-${value}`"
            class="filter-chip"
          >
            <span>{{ `${prefix}${value}${suffix}` }}</span>

            <button
              class="filter-chip__button"
              type="button"
              @click="$emit('toggle-filter', { key, option: value })"
            >
              <span
                class="visually-hidden"
                v-text="$string('accessibility.clear')"
              />

              <icon-close />
            </button>
          </div>
        </div>
      </div>
    </template>
    <div
      v-if="itemsCount < 1 && hasActiveFilters"
      class="text-mobile-paragraph-s-bold text-desktop-paragraph-s-bold"
      v-html="noResultMessage"
    />
  </div>
</template>

<script>
/**
 * Vue: content-filter (content-filter)
 * -----------------------------------------------------------------------------
 * Filter component for plans to show search and applied filters. Also show
 * current results.
 * @param {Object} filters - All filters for check active/inactive
 * @param {Boolean} showFilter theme setting to show filter
 * @param {Boolean} showSearch theme setting to show search
 * @param {String} searchQuery Search Query used for filtering
 * @param {Number} itemsCount Number of filter results
 * @param {String} noResultMessage - String for no results found message
 * @param {String} prefix - String that can be added before filter value display
 * @param {String} suffix - String that can be added after filter value display
 *
 * @emits update-search-query - event to pass new search query
 * @emits toggle-filter - event on checkbox, toggle filter on/off
 * @emits reset-filters - trigger reset all filters
 */

import { mapActions } from 'pinia';
import useOverlaysStore from '~stores/overlays';

import Btn from '~global/btn/btn';
import IconClose from '~icons/directional-navigation/close.svg';
import IconSearchDesktop from '~icons/general/search-desktop.svg';
import IconFilter from '~icons/general/filter.svg';

export default {
  name: 'ContentFilter',

  components: {
    Btn,
    IconClose,
    IconFilter,
    IconSearchDesktop,
  },

  props: {
    filters: {
      type: Object,
      default: () => ({}),
    },
    showFilter: {
      type: Boolean,
      default: true,
    },
    showSearch: {
      type: Boolean,
      default: true,
    },
    searchQuery: {
      type: String,
      default: '',
    },
    itemsCount: {
      type: Number,
      default: 0,
    },
    noResultMessage: {
      type: String,
      required: true,
    },
    prefix: {
      type: String,
      default: '',
    },
    suffix: {
      type: String,
      default: '',
    },
  },

  emits: {
    'update-search-query': (payload) => typeof payload === 'string',
    'toggle-filter': (payload) => typeof payload === 'object',
    'reset-filters': () => true,
  },

  data() {
    return {
      isActive: false,
    };
  },

  computed: {
    /**
     * Check if there are active filters.
     * @returns {Boolean}
     */
    hasActiveFilters() {
      return Object.keys(this.filters).some(
        (key) => this.filters[key].length > 0,
      );
    },
  },

  methods: {
    /**
     * Map Pinia actions.
     */
    ...mapActions(useOverlaysStore, {
      openOverlay: 'open',
    }),

    /**
     * Opens filter drawer
     */
    openFilterOverlay() {
      this.openOverlay({
        component: 'content-drawer',
        ignoreDismissed: true,
        namespace: 'ContentFilter',
      });
    },
  },
};
</script>

<style lang="scss">
@import './content-filter';
</style>
